import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBrand } from "../../context/BrandContext";
import {
  fetchScouts,
  proposeGamePlan,
  saveScout,
  schemaLabel,
} from "../../components/funtionsBD/tacticalPatternService";
import "./TacticalPatternLibrary.css";
import "./MatchPrepScout.css";

const FIELDS = [
  { field: "hand", options: ["right", "left"] },
  { field: "style", options: ["offensive", "defensive", "allround"] },
  { field: "distance", options: ["near", "mid", "away"] },
  { field: "receive_short", options: ["weak", "ok", "strong"] },
  { field: "receive_long", options: ["weak", "ok", "strong"] },
  { field: "flick_short", options: ["yes", "no"] },
  { field: "weak_wing", options: ["fh", "bh"] },
  { field: "feared_serve", options: ["short_cut", "long_fast", "long_cut", "sidespin", "unknown"] },
];

const CONTEXTS = [
  { score_phase: "opening", set_phase: "early" },
  { score_phase: "building", set_phase: "mid" },
  { score_phase: "close", set_phase: "mid" },
  { score_phase: "match_point", set_phase: "fifth" },
];

export function MatchPrepScout() {
  const { t, i18n } = useTranslation();
  const { themeMode } = useBrand();
  const mode = themeMode === "light" ? "light" : "dark";
  const lang = (i18n.language || "en").slice(0, 2);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [label, setLabel] = useState(params.get("name") || "");
  const [answers, setAnswers] = useState({});
  const [scoutId, setScoutId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [context, setContext] = useState(CONTEXTS[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchScouts()
      .then((data) => {
        const list = data?.data || [];
        const name = params.get("name");
        const hit = name
          ? list.find((s) => String(s.opponent_label).toLowerCase() === String(name).toLowerCase())
          : list[0];
        if (hit) {
          setScoutId(hit.scout_id);
          setLabel(hit.opponent_label || "");
          setAnswers(hit.answers || {});
        }
      })
      .catch(() => {});
  }, [params]);

  const nextField = useMemo(
    () => FIELDS.find((f) => !answers[f.field]),
    [answers]
  );

  const persist = async (nextAnswers) => {
    setBusy(true);
    setError("");
    try {
      const saved = await saveScout({
        scout_id: scoutId,
        opponent_label: label || t("schemaOpponent", "Opponent"),
        opponent_player_id: params.get("playerId") || null,
        championship_match_id: params.get("matchId") || null,
        answers: nextAnswers,
      });
      setScoutId(saved.scout_id);
      setAnswers(saved.answers || nextAnswers);
      return saved;
    } catch (err) {
      setError(err?.message || t("error"));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const onPick = async (field, value) => {
    const next = { ...answers, [field]: value };
    setAnswers(next);
    await persist(next);
  };

  const onPropose = async () => {
    setBusy(true);
    setError("");
    try {
      let id = scoutId;
      if (!id) {
        const saved = await persist(answers);
        id = saved?.scout_id;
      }
      const data = await proposeGamePlan({
        scout_id: id,
        context,
        lang,
      });
      setPlan(data);
    } catch (err) {
      setError(err?.message || t("error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`tpl mps tpl--${mode}`}>
      <header className="tpl__head">
        <button type="button" className="mps__back" onClick={() => navigate("/tactical")}>
          {t("back")}
        </button>
        <p className="tpl__kicker">{t("schemaPrepKicker", "Pingpro · Match prep")}</p>
        <h1>{t("schemaPrepTitle", "Scout the opponent")}</h1>
      </header>
      <label className="mps__label">
        {t("schemaOpponent", "Opponent")}
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t("schemaOpponentPh", "Name")}
        />
      </label>
      <div className="mps__q">
        {FIELDS.map((q) => (
          <div key={q.field} className={`mps-q ${answers[q.field] ? "is-done" : nextField?.field === q.field ? "is-now" : ""}`}>
            <p>{t(`schemaQ_${q.field}`, q.field.replace(/_/g, " "))}</p>
            <div className="mps-q__opts">
              {q.options.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={answers[q.field] === opt ? "is-on" : ""}
                  onClick={() => onPick(q.field, opt)}
                  disabled={busy}
                >
                  {t(`schemaOpt_${opt}`, opt)}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mps__ctx">
        {CONTEXTS.map((c) => (
          <button
            key={`${c.score_phase}-${c.set_phase}`}
            type="button"
            className={
              context.score_phase === c.score_phase && context.set_phase === c.set_phase
                ? "is-on"
                : ""
            }
            onClick={() => setContext(c)}
          >
            {t(`schemaCtx_${c.score_phase}`, c.score_phase)}
          </button>
        ))}
      </div>
      <button type="button" className="tpl__prep" onClick={onPropose} disabled={busy || !label}>
        {t("schemaPropose", "Propose game plan")}
      </button>
      {error ? <p className="tpl__err">{error}</p> : null}
      {plan ? (
        <section className="mps-plan">
          <h2>
            {schemaLabel(plan.primary_schema_key, lang)}
            {plan.backup_schema_key
              ? ` · ${schemaLabel(plan.backup_schema_key, lang)}`
              : ""}
          </h2>
          {(plan.rationale_lines || [plan.rationale]).map((line) => (
            <p key={line}>{line}</p>
          ))}
        </section>
      ) : null}
    </div>
  );
}

export default MatchPrepScout;
